import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, X, Info } from 'lucide-react';

// Multi-select AI disclosure form used inside the upload flow + the post-publish
// retag dialog. Builds the structured `ai_disclosure` object the backend expects.
//
// Categories: ai_music, ai_vocals, ai_video, ai_assisted, plus a special "none"
// flag that — if checked — causes the backend to reject the upload entirely.

// Tooltip that opens on hover (desktop) or tap (mobile) and dismisses on
// mouse-leave / click-outside. Renders a small arrow pointing at the trigger.
const InfoTooltip = ({ text, testId = 'info-tooltip' }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label="More info"
        className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-zinc-500 hover:text-zinc-300 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-500"
        data-testid={`${testId}-trigger`}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-2 w-64 max-w-[80vw] rounded-md bg-zinc-950 border border-zinc-700 px-3 py-2 text-[12px] text-zinc-200 leading-snug shadow-xl"
          data-testid={`${testId}-content`}
        >
          {/* Arrow */}
          <span
            aria-hidden="true"
            className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-zinc-950 border-l border-t border-zinc-700 rotate-45"
          />
          {text}
        </span>
      )}
    </span>
  );
};

const CATEGORIES = [
  { key: 'ai_music', label: 'AI-generated music', helper: 'The song itself was made with an AI model.' },
  { key: 'ai_vocals', label: 'AI-generated vocals', helper: 'Vocals synthesised or cloned by AI.' },
  { key: 'ai_video', label: 'AI-generated video', helper: 'The visuals were produced by an AI model.' },
  { key: 'ai_assisted', label: 'AI-assisted', helper: 'Editing, enhancement, upscaling, stems, etc.' },
];

export const initialDisclosure = () => ({
  none: false,
  ai_music: { used: false, tool: '', tool_other: '' },
  ai_vocals: { used: false, tool: '', tool_other: '' },
  ai_video: { used: false, tool: '', tool_other: '' },
  ai_assisted: { used: false, tool: '', tool_other: '' },
});

export const isDisclosureComplete = (d) => {
  if (!d || d.none) return false;
  const anyUsed = CATEGORIES.some((c) => d[c.key]?.used);
  if (!anyUsed) return false;
  for (const c of CATEGORIES) {
    const e = d[c.key];
    if (!e?.used) continue;
    if (!e.tool) return false;
    if (e.tool === 'other' && !(e.tool_other || '').trim()) return false;
  }
  return true;
};

export const AIDisclosureForm = ({ value, onChange, catalog }) => {
  const v = value || initialDisclosure();

  const toggle = (cat) => {
    if (cat === 'none') {
      // Selecting "None" clears everything else
      onChange({ ...initialDisclosure(), none: !v.none });
      return;
    }
    const next = { ...v, none: false, [cat]: { ...v[cat], used: !v[cat].used } };
    onChange(next);
  };

  const setTool = (cat, tool) => {
    onChange({ ...v, [cat]: { ...v[cat], tool, tool_other: tool === 'other' ? v[cat].tool_other : '' } });
  };

  const setOther = (cat, text) => {
    onChange({ ...v, [cat]: { ...v[cat], tool_other: text } });
  };

  return (
    <div className="space-y-3" data-testid="ai-disclosure-form">
      <div className="flex items-start gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-white mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-white flex items-center gap-1.5">
            <span>What's AI in this video?</span>
            <InfoTooltip
              testId="ai-tools-info"
              text="Select every AI tool you used. Listeners can filter and search by tools, so accurate tags help your video get discovered."
            />
          </p>
          <p className="text-xs text-zinc-500">Required. Select all that apply.</p>
        </div>
      </div>

      {CATEGORIES.map((cat) => {
        const entry = v[cat.key] || { used: false, tool: '', tool_other: '' };
        const tools = (catalog && catalog[cat.key]) || [];
        return (
          <div key={cat.key} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <span className="relative inline-flex items-center justify-center mt-0.5 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={entry.used}
                  onChange={() => toggle(cat.key)}
                  className="peer sr-only"
                  data-testid={`disclosure-${cat.key}-checkbox`}
                  disabled={v.none}
                />
                <span
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    entry.used
                      ? 'bg-white border-white'
                      : 'bg-transparent border-zinc-500 peer-hover:border-zinc-300'
                  } ${v.none ? 'opacity-40' : ''}`}
                  aria-hidden="true"
                >
                  {entry.used && (
                    <svg className="w-3.5 h-3.5 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
              </span>
              <span className="flex-1 min-w-0">
                <span className="text-sm text-white font-medium block">{cat.label}</span>
                <span className="text-[11px] text-zinc-500 block">{cat.helper}</span>
              </span>
            </label>
            {entry.used && (
              <div className="mt-2.5 pl-7 space-y-2">
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                  Which tool did you use?
                </label>
                <select
                  value={entry.tool}
                  onChange={(e) => setTool(cat.key, e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-md text-sm text-white px-2.5 py-2"
                  data-testid={`disclosure-${cat.key}-tool`}
                >
                  <option value="">Select tool…</option>
                  {tools.map((t) => (
                    <option key={t.key} value={t.key}>{t.name}</option>
                  ))}
                </select>
                {entry.tool === 'other' && (
                  <input
                    type="text"
                    value={entry.tool_other || ''}
                    onChange={(e) => setOther(cat.key, e.target.value)}
                    placeholder="Name the tool…"
                    maxLength={60}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-md text-sm text-white px-2.5 py-2"
                    data-testid={`disclosure-${cat.key}-other`}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}

      <label className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-zinc-800 bg-zinc-900/20 p-3">
        <span className="relative inline-flex items-center justify-center mt-0.5 flex-shrink-0">
          <input
            type="checkbox"
            checked={!!v.none}
            onChange={() => toggle('none')}
            className="peer sr-only"
            data-testid="disclosure-none-checkbox"
          />
          <span
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              v.none ? 'bg-white border-white' : 'bg-transparent border-zinc-500 peer-hover:border-zinc-300'
            }`}
            aria-hidden="true"
          >
            {v.none && (
              <svg className="w-3.5 h-3.5 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
        </span>
        <span className="flex-1 min-w-0">
          <span className="text-sm text-white font-medium block">None — this work contains no AI elements</span>
          <span className="text-[11px] text-zinc-500 block">
            Heads up: AUDIVUE only accepts AI-generated or AI-assisted videos. Selecting this will block your upload.
          </span>
        </span>
      </label>
    </div>
  );
};

// Loads the per-category catalog from the backend, falls back to empty.
export const useToolsCatalog = (axiosInstance) => {
  const [catalog, setCatalog] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/tools/catalog');
        if (!cancelled) setCatalog(res.data || {});
      } catch {
        if (!cancelled) setCatalog({});
      }
    })();
    return () => { cancelled = true; };
  }, [axiosInstance]);
  return catalog;
};

// Read-only badges displayed under video titles. Tappable → /tools/:key
export const AIDisclosureBadges = ({ disclosure, onTap, dense = false, className = '' }) => {
  if (!disclosure || disclosure.needs_disclosure) return null;
  const items = [];
  ['ai_music', 'ai_vocals', 'ai_video', 'ai_assisted'].forEach((cat) => {
    const e = disclosure[cat];
    if (e?.used && e.tool && e.tool !== 'prefer_not_to_say') {
      const name = e.tool === 'other' ? (e.tool_other || 'Other') : e.tool.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      items.push({ cat, tool: e.tool, name });
    }
  });
  if (items.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="ai-disclosure-badges">
      <span className={`text-zinc-500 ${dense ? 'text-[10px]' : 'text-[11px]'} font-medium uppercase tracking-wider mr-0.5`}>
        Made with
      </span>
      {items.map((b, i) => (
        <React.Fragment key={`${b.cat}-${b.tool}-${i}`}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onTap && onTap(b.tool); }}
            className={`inline-flex items-center gap-1 rounded-full bg-zinc-800/70 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors ${
              dense ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]'
            }`}
          >
            <Sparkles className={dense ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
            <span>{b.name}</span>
          </button>
          {i < items.length - 1 && <span className="text-zinc-600 text-[10px]">·</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

// Retag dialog (post-publish edit). Self-contained — wraps AIDisclosureForm.
export const RetagDisclosureModal = ({ open, onClose, video, axiosInstance, onSaved }) => {
  const [disclosure, setDisclosure] = useState(() => {
    if (video?.ai_disclosure && !video.ai_disclosure.needs_disclosure) {
      return { ...initialDisclosure(), ...video.ai_disclosure };
    }
    return initialDisclosure();
  });
  const catalog = useToolsCatalog(axiosInstance);
  const [saving, setSaving] = useState(false);
  const valid = isDisclosureComplete(disclosure);

  if (!open) return null;
  const submit = async () => {
    setSaving(true);
    try {
      await axiosInstance.patch(`/videos/${video.id}/disclosure`, disclosure);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to update disclosure';
      // eslint-disable-next-line no-alert
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-zinc-950 rounded-2xl border border-zinc-800 max-w-md w-full max-h-[90vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="retag-disclosure-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-white">Edit AI disclosure</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="retag-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Update the AI tools used in <span className="text-zinc-300">{video?.title}</span>. Changes are logged.</p>
        <AIDisclosureForm value={disclosure} onChange={setDisclosure} catalog={catalog || {}} />
        <button
          onClick={submit}
          disabled={!valid || saving}
          className="mt-4 w-full bg-white text-black font-semibold rounded-full py-2.5 text-sm disabled:opacity-50"
          data-testid="retag-save-btn"
        >
          {saving ? 'Saving…' : 'Save disclosure'}
        </button>
      </div>
    </div>
  );
};
